// frontend/src/components/common/UI/Tabs.tsx

import React, { createContext, useContext, useState, ReactNode } from 'react'
import { cn } from '@/utils/helpers'

// ============================================
// Контекст для вкладок
// ============================================

interface TabsContextType {
  activeTab: string
  setActiveTab: (value: string) => void
}

const TabsContext = createContext<TabsContextType | undefined>(undefined)

const useTabs = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs component')
  }
  return context
}

// ============================================
// Компонент Tabs (контейнер)
// ============================================

interface TabsProps {
  value: string
  onChange: (value: string) => void
  children: ReactNode
  className?: string
  variant?: 'default' | 'pills' | 'underline'
}

export const Tabs: React.FC<TabsProps> = ({
  value,
  onChange,
  children,
  className,
  variant = 'default'
}) => {
  return (
    <TabsContext.Provider value={{ activeTab: value, setActiveTab: onChange }}>
      <div className={cn('w-full', className)}>
        <div
          className={cn(
            'flex',
            variant === 'pills' && 'gap-1 p-1 bg-secondary-100 rounded-lg',
            variant === 'underline' && 'border-b border-secondary-200',
            variant === 'default' && 'border-b border-secondary-200'
          )}
        >
          {children}
        </div>
      </div>
    </TabsContext.Provider>
  )
}

// ============================================
// Компонент Tab (кнопка вкладки)
// ============================================

interface TabProps {
  value: string
  children: ReactNode
  className?: string
  disabled?: boolean
  icon?: ReactNode
}

export const Tab: React.FC<TabProps> = ({
  value,
  children,
  className,
  disabled = false,
  icon
}) => {
  const { activeTab, setActiveTab } = useTabs()
  const isActive = activeTab === value

  const variants = {
    default: cn(
      'px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px',
      isActive
        ? 'border-primary-600 text-primary-600'
        : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300',
      disabled && 'opacity-50 cursor-not-allowed'
    ),
    pills: cn(
      'px-4 py-2 text-sm font-medium transition-all rounded-lg',
      isActive
        ? 'bg-white text-secondary-900 shadow-sm'
        : 'text-secondary-500 hover:text-secondary-700 hover:bg-secondary-200/50',
      disabled && 'opacity-50 cursor-not-allowed'
    ),
    underline: cn(
      'px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px',
      isActive
        ? 'border-primary-600 text-primary-600'
        : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300',
      disabled && 'opacity-50 cursor-not-allowed'
    )
  }

  return (
    <button
      onClick={() => !disabled && setActiveTab(value)}
      disabled={disabled}
      className={cn(variants.default, className)}
    >
      <span className="flex items-center gap-2">
        {icon && <span className="w-4 h-4">{icon}</span>}
        {children}
      </span>
    </button>
  )
}

// ============================================
// Компонент TabPanel (контент вкладки)
// ============================================

interface TabPanelProps {
  value: string
  children: ReactNode
  className?: string
}

export const TabPanel: React.FC<TabPanelProps> = ({
  value,
  children,
  className
}) => {
  const { activeTab } = useTabs()

  if (activeTab !== value) return null

  return (
    <div className={cn('animate-fade-in', className)}>
      {children}
    </div>
  )
}

// ============================================
// Компонент TabList (для вертикальных вкладок)
// ============================================

interface TabListProps {
  children: ReactNode
  className?: string
  orientation?: 'horizontal' | 'vertical'
}

export const TabList: React.FC<TabListProps> = ({
  children,
  className,
  orientation = 'horizontal'
}) => {
  return (
    <div
      className={cn(
        'flex',
        orientation === 'vertical' ? 'flex-col' : 'flex-row',
        className
      )}
    >
      {children}
    </div>
  )
}

// ============================================
// Компонент TabPanels (контейнер для панелей)
// ============================================

interface TabPanelsProps {
  children: ReactNode
  className?: string
}

export const TabPanels: React.FC<TabPanelsProps> = ({
  children,
  className
}) => {
  return (
    <div className={cn('mt-4', className)}>
      {children}
    </div>
  )
}

// ============================================
// Хук для управления вкладками
// ============================================

export const useTabsState = (defaultTab: string = '') => {
  const [activeTab, setActiveTab] = useState(defaultTab)

  return {
    activeTab,
    setActiveTab,
    tabsProps: {
      value: activeTab,
      onChange: setActiveTab
    }
  }
}

// ============================================
// Пример использования:
// ============================================

/*
import { Tabs, Tab, TabPanel, useTabsState } from '@/components/common/UI/Tabs'

// Пример 1: Базовые вкладки
const BasicExample = () => {
  const tabs = useTabsState('tab1')

  return (
    <Tabs {...tabs.tabsProps}>
      <Tab value="tab1">Вкладка 1</Tab>
      <Tab value="tab2">Вкладка 2</Tab>
      <Tab value="tab3">Вкладка 3</Tab>
      
      <TabPanel value="tab1">Содержимое вкладки 1</TabPanel>
      <TabPanel value="tab2">Содержимое вкладки 2</TabPanel>
      <TabPanel value="tab3">Содержимое вкладки 3</TabPanel>
    </Tabs>
  )
}

// Пример 2: Вкладки в стиле "Pills"
const PillsExample = () => {
  const tabs = useTabsState('tab1')

  return (
    <Tabs {...tabs.tabsProps} variant="pills">
      <Tab value="tab1">Вкладка 1</Tab>
      <Tab value="tab2">Вкладка 2</Tab>
      <Tab value="tab3">Вкладка 3</Tab>
      
      <TabPanel value="tab1">Содержимое вкладки 1</TabPanel>
      <TabPanel value="tab2">Содержимое вкладки 2</TabPanel>
      <TabPanel value="tab3">Содержимое вкладки 3</TabPanel>
    </Tabs>
  )
}

// Пример 3: С иконками
const WithIconsExample = () => {
  const tabs = useTabsState('profile')

  return (
    <Tabs {...tabs.tabsProps}>
      <Tab value="profile" icon={<UserIcon />}>Профиль</Tab>
      <Tab value="settings" icon={<SettingsIcon />}>Настройки</Tab>
      
      <TabPanel value="profile">Содержимое профиля</TabPanel>
      <TabPanel value="settings">Содержимое настроек</TabPanel>
    </Tabs>
  )
}
*/